import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Conn } from '../conn';
import {
  listMarkdownFiles, getMarkdownFile, putMarkdownFile, deleteMarkdownFile,
  type MarkdownFileInfo,
} from '../api';

export default function ContextPage({ conn }: { conn: Conn }) {
  const [files, setFiles] = useState<MarkdownFileInfo[]>([]);
  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!conn.apiKey) { setErr('Connect an API key (sidebar).'); return; }
    setErr('');
    try { setFiles(await listMarkdownFiles(conn.baseUrl, conn.apiKey)); }
    catch (e) { setErr(String(e)); }
  }, [conn.baseUrl, conn.apiKey]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => files.filter((f) => f.name.toLowerCase().includes(q.toLowerCase())), [files, q]);

  async function edit(n: string) {
    setBusy(true); setErr('');
    try { const f = await getMarkdownFile(conn.baseUrl, conn.apiKey, n); setName(n); setContent(f.content); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }
  async function save() {
    let n = name.trim(); if (!n) return; if (!n.endsWith('.md')) n += '.md';
    setBusy(true); setErr('');
    try { await putMarkdownFile(conn.baseUrl, conn.apiKey, n, content); setName(''); setContent(''); await load(); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }
  async function remove(n: string) {
    setBusy(true); setErr('');
    try { await deleteMarkdownFile(conn.baseUrl, conn.apiKey, n); await load(); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }

  const fmt = (s?: string) => (s ? s.replace('T', ' ').slice(0, 16) : '');

  return (
    <div className="page">
      <header className="page-head"><h1>Context &amp; Files</h1><button onClick={load} disabled={busy}>Refresh</button></header>
      {err && <p className="err">{err}</p>}
      <p className="muted small">Reference a document anywhere in an agent or skill as <code>@{'{'}name{'}'}.md</code> — it's inlined at run time. Stored per-account (Cloudflare Files / S3).</p>

      <div className="toolbar">
        <div className="search-wrap"><input placeholder="Search files…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <span className="count-pill">{filtered.length} files</span>
        <button onClick={() => { setName(''); setContent(''); }}>+ New file</button>
      </div>

      {filtered.length === 0
        ? <div className="empty">No context documents{q ? ' match' : ' yet'}. Create one below — e.g. <code>style_guide.md</code>.</div>
        : <div className="browse-grid">
            {filtered.map((f) => (
              <div key={f.name} className="browse-card" onClick={() => edit(f.name)}>
                <div className="bc-title"><span className="ico">@</span>{f.name}</div>
                <div className="bc-desc">Reference as <code>@{f.name}</code></div>
                <div className="bc-meta"><span className="badge">{f.size} b</span><span className="badge">{fmt(f.modified_at)}</span></div>
                <div className="bc-actions">
                  <button className="link grow" onClick={(e) => { e.stopPropagation(); edit(f.name); }}>Edit</button>
                  <button className="link danger" onClick={(e) => { e.stopPropagation(); remove(f.name); }}>Delete</button>
                </div>
              </div>
            ))}
          </div>}

      <div className="panel">
        <div className="panel-head"><b>{name ? <code>@{name}</code> : 'New @file.md'}</b><button onClick={save} disabled={busy || !name.trim()}>Save</button></div>
        <input placeholder="style_guide.md" value={name} onChange={(e) => setName(e.target.value)} style={{ marginBottom: 10 }} />
        <textarea className="editor" placeholder="# Brand voice&#10;Write concisely…" value={content} onChange={(e) => setContent(e.target.value)} />
      </div>

      <div className="panel">
        <div className="panel-head"><b>@key → value pairs</b><span className="badge">backend pending</span></div>
        <p className="muted small">Inline key/value context (<code>@company_name → Dataxcelerator</code>) resolves at run time alongside <code>@file.md</code>. The key/value store is a backend addition (design §10); use <code>@file.md</code> documents above for now.</p>
      </div>
    </div>
  );
}
